import type {
  PoaIdType,
  PoaReportIdFailReason,
  PoaReportIdType,
  PoaSubmission,
  ProofOfAgePayload,
  RsaRefusalReason,
} from '../model/poa.types'
import type { CrateItem } from '@/entities/order/model'
import { RESTRICTED_AREA_CODES } from './order.poa'

const NOT_APPLICABLE = 'not_applicable' as const

/** Flow vocabulary → the report's, for the ID actually sighted. */
const REPORT_ID_TYPE: Record<PoaIdType, PoaReportIdType> = {
  'drivers-licence': 'drivers_licence',
  'learners-permit': 'learners_permit',
  'passport': 'passport',
  'proof-of-age-card': 'proof_of_age_card',
}

const REPORT_ID_FAIL_REASON: Record<string, PoaReportIdFailReason> = {
  'under-18': 'under_18',
  'no-valid-id': 'no_valid_id',
}

const REPORT_REFUSAL_REASON: Record<Exclude<RsaRefusalReason, 'other'>, string> = {
  'suspected-intoxication': 'suspected_intoxication',
  'suspected-secondary-supply': 'suspected_secondary_supply',
}

/**
 * Reduces the local Proof of Age working record to the payload the backend accepts
 * (BD-8253), shaped for the scheduled Coles report (BD-8254).
 *
 * Every field always carries a value: where a check did not apply to this order the
 * report expects `not_applicable` rather than an absent key, so a column is never
 * blank for an ambiguous reason.
 *
 * Team member initials and per-outcome timestamps are intentionally dropped — the
 * initials are already written to the order by the existing `/tmInitials` patch, and
 * the completion event carries its own time downstream. Keeping the local record
 * richer than the wire payload means the in-progress flow can still show and revise
 * answers without that shape leaking into the API contract.
 */
export function toProofOfAgePayload(
  submission: PoaSubmission,
  scannedCrates?: readonly CrateItem[],
): ProofOfAgePayload {
  const { rsaOutcome, rsaRefusal, ageOutcome, idType, idFailureReason } = submission
  // The caller supplies this order's validated scans. Without scanning, use the
  // category-level handover confirmation. Missing crates never count as supplied.
  const suppliedCategories = scannedCrates === undefined
    ? submission.restrictedCategories
    : scannedCrates.map(crate => RESTRICTED_AREA_CODES[crate.areaCode])
  const restrictedItemsSupplied = suppliedCategories.some(category =>
    submission.restrictedCategories.includes(category)
    && !submission.itemsMarkedForRemoval.includes(category),
  )

  // An ID was sighted, or explicitly none was produced. `none_presented` only makes
  // sense once the check has actually failed — before that there is simply no answer.
  const idTypeChecked: PoaReportIdType = idType
    ? REPORT_ID_TYPE[idType]
    : idFailureReason
      ? 'none_presented'
      : NOT_APPLICABLE

  return {
    has_restricted_items: submission.restrictedCategories.length > 0,

    fit_to_receive_restricted_items: rsaOutcome
      ? rsaOutcome === 'passed'
      : NOT_APPLICABLE,

    // The team member's own words when they chose "Other", otherwise the reason code.
    concern: rsaRefusal
      ? rsaRefusal.reason === 'other'
        ? (rsaRefusal.otherReason ?? NOT_APPLICABLE)
        : REPORT_REFUSAL_REASON[rsaRefusal.reason]
      : NOT_APPLICABLE,

    looks_25_or_older: ageOutcome
      ? ageOutcome === 'looks-25-or-older'
      : NOT_APPLICABLE,

    id_check_outcome: idFailureReason
      ? 'failed'
      : idType
        ? 'passed'
        // The customer looked 25 or older, so no ID was needed — distinct from an
        // order where the age check never ran at all.
        : ageOutcome === 'looks-25-or-older'
          ? 'not_required'
          : NOT_APPLICABLE,

    id_type_checked: idTypeChecked,

    id_check_fail_reason: idFailureReason
      ? REPORT_ID_FAIL_REASON[idFailureReason]
      : NOT_APPLICABLE,

    // At least one restricted crate was handed over, after any POA removals.
    restricted_items_supplied: submission.restrictedCategories.length > 0
      ? restrictedItemsSupplied
      : NOT_APPLICABLE,
  }
}
